import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MessageSquare, Mail, Send, StickyNote, GitBranch,
  Sparkles, Loader2, Clock, Plus, Star, DollarSign, Trophy, Flame,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui/page";
import { StageBadge } from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency, fmtDate, fmtDateTime, fmtTime, daysSince } from "@/lib/format";

const ACT_ICON = { CALL: Phone, TEXT: MessageSquare, EMAIL: Mail, MAILER: Send };
const STATUSES = {
  CALL: ["Completed", "No Answer", "Left Voicemail", "Connected"],
  TEXT: ["Sent", "Delivered", "Responded"],
  EMAIL: ["Sent", "Delivered", "Opened", "Responded"],
  MAILER: ["Sent", "Delivered"],
};

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { selected } = useClient();
  const { isReadOnly } = useAuth();
  const qc = useQueryClient();
  const [aiLoading, setAiLoading] = useState(false);

  const cid = selected;
  const { data: c, isLoading } = useQuery({
    queryKey: ["contact", cid, contactId],
    queryFn: async () => (await api.get(`/clients/${cid}/contacts/${contactId}`)).data,
    enabled: !!cid,
  });
  const { data: timeline = [] } = useQuery({
    queryKey: ["timeline", cid, contactId],
    queryFn: async () => (await api.get(`/clients/${cid}/contacts/${contactId}/timeline`)).data,
    enabled: !!cid,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["contact", cid, contactId] });
    qc.invalidateQueries({ queryKey: ["timeline", cid, contactId] });
  };

  const aiMut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${cid}/contacts/${contactId}/ai-summary`)).data,
    onMutate: () => setAiLoading(true),
    onSettled: () => setAiLoading(false),
    onSuccess: () => { toast.success("AI summary generated"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const priorityMut = useMutation({
    mutationFn: async (is_priority) => (await api.post(`/clients/${cid}/contacts/${contactId}/priority`, { is_priority })).data,
    onSuccess: () => refresh(),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading || !c) return <Spinner />;
  const ds = daysSince(c.last_activity_at);

  return (
    <div>
      <button onClick={() => navigate("/contacts")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-contacts">
        <ArrowLeft className="h-4 w-4" /> Contacts
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-xl font-heading font-bold text-slate-900">{c.first_name} {c.last_name}</h1>
                {c.company && <div className="text-sm text-slate-500">{c.company}</div>}
              </div>
              <div className="flex items-center gap-2">
                {c.is_warm && <span data-testid="contact-warm-badge" className="inline-flex items-center gap-0.5 text-[10px] font-bold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1.5 py-0.5"><Flame className="h-3 w-3" /> WARM</span>}
                {["LEAD", "PROSPECT"].includes(c.current_stage) && (
                  <button onClick={() => priorityMut.mutate(!c.is_priority)} data-testid="contact-priority-toggle" title="Mark as priority project">
                    <Star className={`h-5 w-5 ${c.is_priority ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
                  </button>
                )}
                <StageBadge stage={c.current_stage} />
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Phone" value={c.mobile_phone} />
              <Row label="Email" value={c.email} />
              <Row label="Address" value={[c.address, c.city, c.state, c.zip].filter(Boolean).join(", ")} />
              <Row label="Source" value={c.lead_source || c.source} />
              <Row label="Salesperson" value={c.salesperson} />
              <Row label="Original Lead Date" value={fmtDate(c.original_lead_date)} />
              {c.referred_by_name && <Row label="Referred By" value={c.referred_by_name} />}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
              <Stat label="Est. Value" value={c.estimated_value ? fmtCurrency(c.estimated_value) : "—"} />
              <Stat label="Actual Deal" value={c.actual_deal_value ? fmtCurrency(c.actual_deal_value) : "—"} />
              <Stat label="Last Activity" value={c.last_activity_at ? `${fmtDate(c.last_activity_at)}` : "None"} sub={c.last_activity_at ? `${ds} days ago` : ""} danger={ds > 14} />
              <Stat label="Workflow" value={c.current_workflow || "None"} sub={c.automation_active ? "Automation ON" : "Automation OFF"} />
            </div>
            {!isReadOnly && (
              <div className="mt-4 flex gap-2">
                <StageChangeDialog clientId={cid} contactId={contactId} current={c.current_stage} onDone={refresh} />
              </div>
            )}
            {isReadOnly && !["WON", "LOST"].includes(c.current_stage) && (
              <div className="mt-4">
                <MarkWonButton clientId={cid} contactId={contactId} onDone={refresh} />
              </div>
            )}
            {["DEAL", "WON"].includes(c.current_stage) && (
              <div className="mt-3">
                <EditDealValueDialog clientId={cid} contactId={contactId} current={c.actual_deal_value} onDone={refresh} />
              </div>
            )}
          </div>

          {/* AI Summary */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-semibold"><Sparkles className="h-4 w-4 text-blue-400" /> AI Summary</div>
              <Button size="sm" variant="secondary" onClick={() => aiMut.mutate()} disabled={aiLoading} data-testid="ai-summary-btn">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
              </Button>
            </div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed" data-testid="ai-summary-text">
              {c.ai_summary || "Generate an AI summary of this contact's status, engagement and recommended next action."}
            </p>
          </div>
        </div>

        {/* Right: timeline + actions */}
        <div className="lg:col-span-2 space-y-4">
          {!isReadOnly && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-2">
              <LogActivityDialog clientId={cid} contactId={contactId} onDone={refresh} />
              <AddNoteDialog clientId={cid} contactId={contactId} onDone={refresh} />
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Timeline</h2>
            {timeline.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No activity yet. Log a call, text, email or note.</div>
            ) : (
              <div className="relative pl-6">
                <div className="absolute left-[9px] top-1 bottom-1 w-px bg-slate-200" />
                {timeline.map((it, i) => <TimelineItem key={i} it={it} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (<div className="flex gap-3"><span className="w-28 text-slate-400 shrink-0">{label}</span><span className="text-slate-700 font-medium break-words">{value || "—"}</span></div>);
}
function Stat({ label, value, sub, danger }) {
  return (<div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div><div className={`font-heading font-bold ${danger ? "text-rose-600" : "text-slate-900"}`}>{value}</div>{sub && <div className="text-[11px] text-slate-400">{sub}</div>}</div>);
}

function TimelineItem({ it }) {
  if (it.kind === "stage") {
    return (
      <div className="relative pb-5">
        <span className="absolute -left-[19px] top-0.5 h-[18px] w-[18px] rounded-full bg-violet-100 border-2 border-white grid place-items-center"><GitBranch className="h-2.5 w-2.5 text-violet-600" /></span>
        <div className="text-xs text-slate-400">{fmtDateTime(it.at)}</div>
        <div className="text-sm text-slate-700 mt-0.5"><span className="font-semibold">Stage changed</span> {it.previous_stage ? `${it.previous_stage} → ` : "Created as "}<span className="font-semibold">{it.new_stage}</span> {it.changed_by_name ? `· ${it.changed_by_name}` : ""}</div>
      </div>
    );
  }
  if (it.kind === "note") {
    return (
      <div className="relative pb-5">
        <span className="absolute -left-[19px] top-0.5 h-[18px] w-[18px] rounded-full bg-amber-100 border-2 border-white grid place-items-center"><StickyNote className="h-2.5 w-2.5 text-amber-600" /></span>
        <div className="text-xs text-slate-400">{fmtDateTime(it.at)} · {it.user_name}</div>
        <div className="text-sm text-slate-700 mt-0.5">{it.text}</div>
      </div>
    );
  }
  const Icon = ACT_ICON[it.type] || Clock;
  return (
    <div className="relative pb-5">
      <span className="absolute -left-[19px] top-0.5 h-[18px] w-[18px] rounded-full bg-blue-100 border-2 border-white grid place-items-center"><Icon className="h-2.5 w-2.5 text-blue-600" /></span>
      <div className="text-xs text-slate-400">{fmtDate(it.at)} · {fmtTime(it.at)}</div>
      <div className="text-sm text-slate-700 mt-0.5">
        <span className="font-semibold">{it.type}</span>{it.status ? ` — ${it.status}` : ""} {it.automated ? <span className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded ml-1">AUTO</span> : ""}
        {it.notes && <div className="text-slate-500">{it.notes}</div>}
      </div>
    </div>
  );
}

function StageChangeDialog({ clientId, contactId, current, onDone }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(current);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/contacts/${contactId}/stage`, {
      new_stage: stage, actual_deal_value: ["DEAL", "WON"].includes(stage) && value ? Number(value) : null, note: note || null,
    })).data,
    onSuccess: () => { toast.success("Stage updated"); setOpen(false); setNote(""); setValue(""); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setStage(current); }}>
      <DialogTrigger asChild><Button variant="outline" className="w-full" data-testid="change-stage-btn"><GitBranch className="h-4 w-4 mr-1.5" /> Move Stage</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Change Stage</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New Stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="mt-1" data-testid="stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["LEAD", "PROSPECT", "RE_ENGAGED", "DEAL", "WON", "LOST"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", "-")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {["DEAL", "WON"].includes(stage) && (
            <div><Label>Actual Deal Value (optional)</Label><Input data-testid="deal-value-input" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave blank to use average job size" className="mt-1" /></div>
          )}
          <div><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" rows={2} /></div>
          {stage === "DEAL" && <p className="text-xs text-emerald-600">Moving to DEAL starts the Referral Program and stops sales nurture.</p>}
          {stage === "LOST" && <p className="text-xs text-rose-600">LOST stops all outreach and automation.</p>}
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="confirm-stage-btn">Update Stage</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogActivityDialog({ clientId, contactId, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("CALL");
  const [status, setStatus] = useState("Completed");
  const [direction, setDirection] = useState("Outbound");
  const [notes, setNotes] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/contacts/${contactId}/activities`, { type, status, direction, notes, automated: false })).data,
    onSuccess: () => { toast.success("Activity logged"); setOpen(false); setNotes(""); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="log-activity-btn"><Plus className="h-4 w-4 mr-1.5" /> Log Activity</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setStatus(STATUSES[v][0]); }}>
              <SelectTrigger className="mt-1" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["CALL", "TEXT", "EMAIL", "MAILER"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Outcome</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="activity-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES[type].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Outbound">Outbound</SelectItem><SelectItem value="Inbound">Inbound</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="activity-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" /></div>
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-activity-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddNoteDialog({ clientId, contactId, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/contacts/${contactId}/notes`, { text })).data,
    onSuccess: () => { toast.success("Note added"); setOpen(false); setText(""); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="add-note-btn"><StickyNote className="h-4 w-4 mr-1.5" /> Add Note</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
        <Textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Write a note…" />
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={!text || mut.isPending} data-testid="save-note-btn">Save Note</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDealValueDialog({ clientId, contactId, current, onDone }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(current || "");
  const mut = useMutation({
    mutationFn: async () => (await api.put(`/clients/${clientId}/deals/${contactId}`, { actual_deal_value: value ? Number(value) : null })).data,
    onSuccess: () => { toast.success("Deal amount updated"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setValue(current || ""); }}>
      <DialogTrigger asChild><Button variant="outline" className="w-full" data-testid="edit-deal-value-btn"><DollarSign className="h-4 w-4 mr-1.5" /> Edit Deal Value</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Deal Value</DialogTitle></DialogHeader>
        <div><Label>Actual Deal Value</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave blank to use average job size" className="mt-1" data-testid="contact-deal-value-input" /></div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-contact-deal-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkWonButton({ clientId, contactId, onDone }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/contacts/${contactId}/stage`, { new_stage: "WON", actual_deal_value: value ? Number(value) : null })).data,
    onSuccess: () => { toast.success("Marked as Won"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setValue(""); }}>
      <DialogTrigger asChild><Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mark-won-btn"><Trophy className="h-4 w-4 mr-1.5" /> Mark as Won</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Mark as Won</DialogTitle></DialogHeader>
        <div><Label>Deal Value (optional)</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave blank to use average job size" className="mt-1" data-testid="mark-won-value" /></div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="confirm-mark-won-btn">Confirm Won</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
